// Minimal Supabase authentication service - uses standard patterns only
import { supabase } from '../config/supabase';
import { config } from '../config/app';
import type { User } from './types';
import { mapSupabaseUser } from './types';
import { debugLog } from '../utils/debug';

/**
 * Sign in with Google OAuth
 * @deprecated Currently not in use - OAuth providers need proper configuration
 * TODO: Re-enable when Google OAuth is properly set up in production
 */
export const signInWithGoogle = async (): Promise<void> => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: config.auth.redirectUrls.callback
    }
  });
  
  if (error) throw error;
};

/**
 * Sign in with Apple OAuth
 * @deprecated Currently not in use - OAuth providers need proper configuration
 * TODO: Re-enable when Apple OAuth is properly set up in production
 */
export const signInWithApple = async (): Promise<void> => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: config.auth.redirectUrls.callback
    }
  });
  
  if (error) throw error;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) throw error;
  if (!data.user) throw new Error('No user returned');

  return mapSupabaseUser(data.user, { tier: 'free' });
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email: string, password: string): Promise<User> => {
  debugLog.info('Starting signup process for email:', email);
  
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: config.auth.redirectUrls.callback
      }
    });

    debugLog.info('Supabase signup response:', {
      user: data.user ? { id: data.user.id, email: data.user.email, email_confirmed_at: data.user.email_confirmed_at } : null,
      session: data.session ? 'session_exists' : 'no_session',
      error: error ? { message: error.message, status: error.status } : null,
      redirectUrl: config.auth.redirectUrls.callback
    });

    if (error) {
      debugLog.error('Signup error:', error);
      throw error;
    }
    if (!data.user) {
      debugLog.error('No user returned from signup');
      throw new Error('No user returned');
    }

    debugLog.success('Signup successful, user created:', data.user.id);
    return mapSupabaseUser(data.user, { tier: 'free' });
  } catch (err) {
    debugLog.error('Signup exception:', err);
    throw err;
  }
};

/**
 * Sign in with phone (SMS OTP)
 */
export const signInWithPhone = async (phone: string): Promise<void> => {
  const { error } = await supabase.auth.signInWithOtp({ phone });
  if (error) throw error;
};

/**
 * Verify phone with OTP
 */
export const verifyPhone = async (phone: string, token: string): Promise<User> => {
  const { data, error } = await supabase.auth.verifyOtp({
    phone,
    token,
    type: 'sms'
  });

  if (error) throw error;
  if (!data.user) throw new Error('No user returned');

  return mapSupabaseUser(data.user, { tier: 'free' });
};

/**
 * Resend email confirmation
 */
export const resendConfirmation = async (email: string): Promise<void> => {
  debugLog.info('Starting resend confirmation for email:', email);
  
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    });
    
    if (error) {
      debugLog.error('Resend confirmation error:', {
        message: error.message,
        status: error.status,
        details: error
      });
      throw error;
    }
    
    debugLog.success('Resend confirmation successful for email:', email);
  } catch (err) {
    debugLog.error('Resend confirmation exception:', err);
    throw err;
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

/**
 * Get current user
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  return user ? mapSupabaseUser(user, { tier: 'free' }) : null;
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
    if (session?.user) {
      try {
        // Fetch user profile to get the actual tier
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('tier, stripe_customer_id, subscription_status')
          .eq('id', session.user.id)
          .single();

        let profileData = profile;

        if (error) {
          // If profile doesn't exist, create it with default tier
          if (error.code === 'PGRST116') {
            const { error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                email: session.user.email,
                display_name: session.user.user_metadata?.display_name || session.user.user_metadata?.name,
                avatar_url: session.user.user_metadata?.avatar_url,
                tier: 'free'
              });
            
            if (insertError) {
              // Silently handle profile creation error
            }
            
            // Use default free tier
            profileData = { 
              tier: 'free', 
              stripe_customer_id: null, 
              subscription_status: null
            };
          } else {
            // Silently handle profile fetch error, default to free
            profileData = { 
              tier: 'free', 
              stripe_customer_id: null, 
              subscription_status: null
            };
          }
        }

        callback(mapSupabaseUser(session.user, profileData));
      } catch {
        // Silently handle any errors, default to free tier
        callback(mapSupabaseUser(session.user, { tier: 'free' }));
      }
    } else {
      callback(null);
    }
  });

  return () => subscription.unsubscribe();
};